import dayjs from 'dayjs';
import 'dayjs/locale/es';

import type { SubscriptionEventViewDto } from '@subscriptions/application/dtos';
import type { SubscriptionEventApiResponse } from '@subscriptions/infrastructure/api/types';

/** dayjs format that renders a date as "15 de marzo 2026" under the es locale. */
const CUTOFF_DATE_FORMAT = 'D [de] MMMM YYYY';

/** Mapper type for collection-status event API transformations. */
export type SubscriptionEventsApiMapperType = {
  mapApiResponseToViewDto: (response: SubscriptionEventApiResponse) => SubscriptionEventViewDto;
  mapApiResponseListToViewDtos: (response: SubscriptionEventApiResponse[]) => SubscriptionEventViewDto[];
};

/** Formats a raw cutoff date as "15 de marzo 2026", returning '' for empty/invalid input. */
const formatCutoffDate = (raw: string | null): string => {
  if (!raw) return '';
  const parsed = dayjs(raw).locale('es');
  return parsed.isValid() ? parsed.format(CUTOFF_DATE_FORMAT) : '';
};

/** Extracts the day of the month from a raw date, returning null for empty/invalid input. */
const extractPaymentDay = (raw: string): number | null => {
  if (!raw) return null;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.date() : null;
};

/**
 * Creates the mapper for collection-status event API type conversions.
 *
 * - `paymentDateCutoff` and `posCutoffDate` are formatted as "15 de marzo 2026" (es locale).
 * - `paymentDay` is the day of the month of `paymentDate` (WARNING banner `{{day}}`).
 * - `subscriptionsCount` is derived from `subscriptionUuids.length`.
 * - `daysToCutoff` is passed through (null when it does not apply).
 */
export const createSubscriptionEventsApiMapper = (): SubscriptionEventsApiMapperType => {
  const mapApiResponseToViewDto = (response: SubscriptionEventApiResponse): SubscriptionEventViewDto => ({
    event: response.event,
    paymentDate: response.paymentDate,
    paymentDay: extractPaymentDay(response.paymentDate),
    paymentDateCutoff: formatCutoffDate(response.paymentDateCutoff),
    posCutoffDate: formatCutoffDate(response.posCutoffDate),
    daysToCutoff: response.daysToCutoff,
    subscriptionsCount: response.subscriptionUuids.length,
  });

  const mapApiResponseListToViewDtos = (response: SubscriptionEventApiResponse[]): SubscriptionEventViewDto[] =>
    response.map(mapApiResponseToViewDto);

  return { mapApiResponseToViewDto, mapApiResponseListToViewDtos };
};
